import { useCallback, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'

import { AuditOperation, AuditSource, Role, type AuditLog, type BillingAuditFilter } from 'common-billing-server/types'
import type { Query } from 'common/query'
import { AuditLogOperationResult, type SortOrder } from 'common/api/v1/types'
import { BILLING_AUDIT_ENTITY_LABELS, isBillingAuditEntity } from 'common-billing-server/auditEntities'
import { AuditChanges } from 'common-ui/components/AuditChanges'
import { FilterType } from 'common-ui/components/Filter'
import { useBillingApi } from '../../api/billing/billing-context'
import { formatDateTime } from '../../format-date'
import Wrapper from '../common/Wrapper'
import {
    type Column,
    DataFetchingPaginatedTable,
    type FieldFilterConfig,
    type RefreshableDataFetchingPaginatedTable,
} from '../common/Table'
import type { PaginatedDataFetchingFilter } from '../common/hooks/hook-paginated-data-fetching'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import { useAuth } from '../login/Auth'

type AuditLogSortField = never
type AuditLogBooleanFilter = Record<string, never>
type AuditLogFieldFilter = {
    fromDate: string
    toDate: string
    source: string
    entity: string
    entityName: string
    username: string
    customerName: string
    operation: string
    result: string
}

const humanize = (value: string) => {
    const words = value.replace(/^console-/, '').replaceAll('-', ' ')
    return words.charAt(0).toUpperCase() + words.slice(1)
}

const humanizeEntity = (value: string) => {
    const label = isBillingAuditEntity(value) ? BILLING_AUDIT_ENTITY_LABELS[value] : humanize(value)
    return value.startsWith('console-') ? `${label} (Console)` : label
}

const entityOptions = Object.keys(BILLING_AUDIT_ENTITY_LABELS)
    .map((value) => ({ name: humanizeEntity(value), value }))
    .sort((left, right) => left.name.localeCompare(right.name))

const sourceLabels: Record<AuditSource, string> = {
    [AuditSource.internal]: 'Internal',
    [AuditSource.external]: 'External',
    [AuditSource.netsuiteSync]: 'NetSuite sync',
}

/** Each source selects only itself; the sync's service account is not counted as internal staff. */
const sourceFilters: Record<AuditSource, Pick<BillingAuditFilter, 'internal' | 'external' | 'netsuiteSync'>> = {
    [AuditSource.internal]: { internal: true, external: false, netsuiteSync: false },
    [AuditSource.external]: { internal: false, external: true, netsuiteSync: false },
    [AuditSource.netsuiteSync]: { internal: false, external: false, netsuiteSync: true },
}

const valuesToOptions = (values: string[]) =>
    [...new Set(values)].sort((left, right) => left.localeCompare(right)).map((value) => ({ name: value, value }))

const parseDateParam = (value: string | null | undefined) => {
    if (!value) return undefined
    const date = new Date(value)
    return Number.isNaN(date.valueOf()) ? undefined : date
}

const entityFieldFilters: Array<FieldFilterConfig<AuditLogFieldFilter>> = [
    {
        filterName: 'source',
        label: 'Source',
        type: FilterType.select,
        options: Object.values(AuditSource).map((source) => ({ name: sourceLabels[source], value: source })),
    },
    { filterName: 'entity', label: 'Entity', type: FilterType.select, options: entityOptions },
    { filterName: 'entityName', label: 'Name', type: FilterType.text },
]
const operationFieldFilter: FieldFilterConfig<AuditLogFieldFilter> = {
    filterName: 'operation',
    label: 'Operation',
    type: FilterType.select,
    options: Object.values(AuditOperation).map((operation) => ({ name: humanize(operation), value: operation })),
}
const resultFieldFilter: FieldFilterConfig<AuditLogFieldFilter> = {
    filterName: 'result',
    label: 'Result',
    type: FilterType.select,
    options: [
        { name: 'Succeeded', value: AuditLogOperationResult.success },
        { name: 'Failed', value: AuditLogOperationResult.error },
    ],
}

export const AuditLogPage = () => {
    const billingApi = useBillingApi()
    const { user } = useAuth()
    const [searchParams] = useSearchParams()
    const fromDateParam = searchParams.get('fromDate')
    const toDateParam = searchParams.get('toDate')
    const fromDate = useMemo(() => parseDateParam(fromDateParam), [fromDateParam])
    const toDate = useMemo(() => parseDateParam(toDateParam), [toDateParam])
    const tableRef = useRef<RefreshableDataFetchingPaginatedTable>(null)
    const { addFailedOperation, errorBanner } = useFailedOperationBanner()
    const [expandedId, setExpandedId] = useState<string>()

    const loadUserOptions = useCallback(
        async (filter?: string) => {
            const query = { limit: 30, ...(filter ? { filter: { username: filter } } : {}) }
            const [{ items: internalUsers }, { items: externalUsers }] = await Promise.all([
                billingApi.listUsers(query),
                billingApi.listExternalUsers(query),
            ])
            return valuesToOptions([...internalUsers, ...externalUsers].map(({ username }) => username))
        },
        [billingApi]
    )

    const loadCustomerOptions = useCallback(
        async (filter?: string) => {
            const { items } = await billingApi.listCustomers({
                limit: 30,
                ...(filter ? { filter: { searchName: filter } } : {}),
            })
            return valuesToOptions(items.map(({ name }) => name))
        },
        [billingApi]
    )

    const fieldFilters = useMemo<Array<FieldFilterConfig<AuditLogFieldFilter>>>(
        () => [
            {
                filterName: 'fromDate',
                label: 'From (UTC)',
                timezone: 'UTC',
                type: FilterType.datetime,
                maxDate: toDate,
                maxDateMessage: 'Cannot be after "To"',
            },
            {
                filterName: 'toDate',
                label: 'To (UTC)',
                timezone: 'UTC',
                type: FilterType.datetime,
                minDate: fromDate,
                minDateMessage: 'Cannot be before "From"',
            },
            ...entityFieldFilters,
            {
                filterName: 'username',
                label: 'User',
                operator: 'matches',
                type: FilterType.autocomplete,
                loadOptions: loadUserOptions,
            },
            {
                filterName: 'customerName',
                label: 'Actor org',
                operator: 'matches',
                type: FilterType.autocomplete,
                loadOptions: loadCustomerOptions,
            },
            operationFieldFilter,
            ...(user?.role === Role.super ? [resultFieldFilter] : []),
        ],
        [fromDate, loadCustomerOptions, loadUserOptions, toDate, user?.role]
    )

    const columns = useMemo<Column<AuditLog, AuditLogSortField>[]>(
        () => [
            {
                title: '',
                isClickable: false,
                valueForColumn: (entry) =>
                    entry.changes?.length || entry.error ? (
                        <IconButton
                            size="small"
                            aria-expanded={expandedId === entry.id}
                            aria-label={`${expandedId === entry.id ? 'Hide' : 'Show'} details for ${entry.entityName || entry.entity}`}
                            onClick={() => setExpandedId((current) => (current === entry.id ? undefined : entry.id))}
                        >
                            {expandedId === entry.id ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                        </IconButton>
                    ) : null,
            },
            {
                title: 'Time (UTC)',
                valueForColumn: ({ createdAt }) => formatDateTime(createdAt),
            },
            { title: 'User', valueForColumn: ({ username }) => username || '—' },
            { title: 'Source', valueForColumn: ({ source }) => sourceLabels[source] },
            {
                title: 'Actor org',
                titleHelp:
                    "The acting user's active external customer. Net Insight staff have none, so internal actions are blank here even when they affect a customer.",
                valueForColumn: ({ customerName, customerId }) => customerName || customerId || '—',
            },
            { title: 'Operation', valueForColumn: ({ operation }) => humanize(operation) },
            { title: 'Entity', valueForColumn: ({ entity }) => (entity ? humanizeEntity(entity) : '—') },
            { title: 'Name', valueForColumn: ({ entityName }) => entityName || '—' },
            {
                title: 'Result',
                valueForColumn: ({ error }) => (error ? 'Failed' : 'Succeeded'),
            },
            {
                title: 'Duration',
                valueForColumn: ({ executionTimeMs }) =>
                    typeof executionTimeMs === 'number' ? `${executionTimeMs} ms` : '—',
            },
        ],
        [expandedId]
    )

    async function fetchAuditLogs(
        query: Query<
            PaginatedDataFetchingFilter<AuditLogBooleanFilter, AuditLogFieldFilter>,
            SortOrder<AuditLogSortField>
        >
    ) {
        try {
            const fieldFilters = query.filter?.fieldFilters
            const source = fieldFilters?.source as AuditSource | undefined
            const from = parseDateParam(fieldFilters?.fromDate)
            const to = parseDateParam(fieldFilters?.toDate)
            const filter: BillingAuditFilter = {
                ...((source && sourceFilters[source]) ?? { internal: true, external: true }),
                ...(from ? { fromDate: from } : {}),
                ...(to ? { toDate: to } : {}),
                ...(fieldFilters?.entity ? { entity: fieldFilters.entity } : {}),
                ...(fieldFilters?.entityName ? { entityName: fieldFilters.entityName } : {}),
                ...(fieldFilters?.username ? { username: fieldFilters.username } : {}),
                ...(fieldFilters?.customerName ? { customerName: fieldFilters.customerName } : {}),
                ...(fieldFilters?.operation ? { operation: fieldFilters.operation } : {}),
                ...(fieldFilters?.result ? { result: fieldFilters.result as AuditLogOperationResult } : {}),
            }
            return await billingApi.listAuditLogs({
                skip: query.skip,
                limit: query.limit,
                filter,
            })
        } catch (error) {
            addFailedOperation({
                id: 'list-audit-logs',
                message: 'Failed fetching audit log',
                error,
                retryFn: tableRef.current?.refreshData,
            })
            throw error
        }
    }

    return (
        <Wrapper fillHeight>
            <DataFetchingPaginatedTable
                stickyHeader
                myRef={tableRef}
                api={fetchAuditLogs}
                columns={columns}
                fieldFilters={fieldFilters}
                searchParameters={{ filterName: 'entityName' }}
                emptyRowsMessage="No audit log entries match the selected filters"
                renderExpandedRow={(entry) =>
                    expandedId === entry.id && (entry.changes?.length || entry.error) ? (
                        <Box sx={{ py: 1, px: 2, display: 'grid', gap: 1 }}>
                            {entry.error ? (
                                <Typography variant="body2" color="error">
                                    {entry.error}
                                </Typography>
                            ) : null}
                            {entry.changes?.length ? <AuditChanges changes={entry.changes} /> : null}
                        </Box>
                    ) : null
                }
            />
            {errorBanner}
        </Wrapper>
    )
}
